import React, { useEffect, useState, useRef } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  styled,
} from "@mui/material";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import DeleteIcon from "@mui/icons-material/Delete";
import { Inventory2, Search, PictureAsPdf, Refresh } from "@mui/icons-material";
import { StockMedicineSkeleton } from "./loading/StockMedicineSkeleton";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import { format } from "date-fns";
import { lightBlue, blueGrey, blue, green } from "@mui/material/colors";

// Custom styled TextField
const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: blueGrey[200],
    },
    "&:hover fieldset": {
      borderColor: blue[300],
    },
    "&.Mui-focused fieldset": {
      borderWidth: "1px",
      borderColor: blue[500],
    },
  },
  "& .MuiInputLabel-root": {
    color: blueGrey[600],
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: blue[600],
  },
}));

// Custom styled Select
const CustomSelect = styled(Select)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: blueGrey[200],
    },
    "&:hover fieldset": {
      borderColor: blue[300],
    },
    "&.Mui-focused fieldset": {
      borderWidth: "1px",
      borderColor: blue[500],
    },
  },
  "& .MuiInputLabel-root": {
    color: blueGrey[600],
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: blue[600],
  },
}));

const StockMedicine = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const tableRef = useRef(null);

  // State for filters
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch data
  const {
    data: stockMedicines = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/?search=${searchQuery}&medicine__category=${categoryFilter}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("stock-medicines", token);

  const { data: medicineCategory = [] } = useFetchObjects(
    ["medicine-categories"],
    `medicine-categories/`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchQuery, categoryFilter, page, rowsPerPage]);

  // Calculate total value
  const totalAmount = stockMedicines.reduce((sum, stock) => {
    const quantity = parseFloat(stock.quantity) || 0;
    const sellPrice = parseFloat(stock.sell_price) || 0;
    return sum + quantity * sellPrice;
  }, 0);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Improved PDF download using jspdf-autotable
  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
    });

    // Add title and date
    doc.setFontSize(18);
    doc.setTextColor(blue[700]);
    doc.text(t("pharmacy.stockMedicineReport"), 15, 15);

    doc.setFontSize(12);
    doc.setTextColor(blueGrey[600]);
    doc.text(`Generated on: ${format(new Date(), "PPPPp")}`, 15, 22);

    // Add summary
    doc.setFontSize(14);
    doc.setTextColor(blue[800]);
    doc.text(
      `Total Inventory Value: ${parseFloat(totalAmount.toFixed(2))} ؋`,
      15,
      30
    );

    // Prepare table data
    const headers = [
      "#",
      t("pharmacy.category"),
      t("pharmacy.medicineName"),
      t("pharmacy.expiryDate"),
      t("pharmacy.quantity"),
      t("pharmacy.sellPrice") + " (؋)",
    ];

    const data = stockMedicines.map((stock, index) => [
      index + 1,
      stock.category_display,
      stock.medicine_display,
      stock.expiry_date,
      stock.quantity,
      stock.sell_price + " ؋",
    ]);

    // Add table
    doc.autoTable({
      head: [headers],
      body: data,
      startY: 35,
      styles: {
        fontSize: 10,
        cellPadding: 2,
        valign: "middle",
        halign: "left", // Changed to left align
        textColor: blueGrey[800],
        fillColor: blueGrey[50],
      },
      headStyles: {
        fillColor: blue[700],
        textColor: 255,
        fontStyle: "bold",
        halign: "left", // Changed to left align
      },
      alternateRowStyles: {
        fillColor: blueGrey[100],
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 25 },
      },
      margin: { top: 35 },
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(blueGrey[500]);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    doc.save(
      `Stock_Medicine_Report_${format(new Date(), "yyyyMMdd_HHmmss")}.pdf`
    );
  };

  if (isLoading) {
    return <StockMedicineSkeleton />;
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography color="error" variant="h6">
          {error.message}
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={refetch}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px)",
        width: "100%",
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          padding: { xs: 2, md: 3 },
          backgroundColor: "white",
          borderBottom: `1px solid ${blueGrey[100]}`,
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Avatar
              sx={{
                bgcolor: lightBlue[50],
                width: 56,
                height: 56,
                color: blue[700],
              }}
            >
              <Inventory2 fontSize="large" />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography fontWeight={700} variant="h4" color="primary">
              {t("pharmacy.stockMedicine")}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t("pharmacy.overview")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("pharmacy.downloadPdf")}>
              <IconButton
                onClick={handleDownloadPDF}
                sx={{
                  backgroundColor: blue[50],
                  "&:hover": { backgroundColor: blue[100] },
                }}
              >
                <PictureAsPdf color="primary" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Search and Filter Section */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <CustomTextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={t("pharmacy.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("pharmacy.filterByCategory")}</InputLabel>
              <CustomSelect
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label={t("pharmacy.filterByCategory")}
              >
                <MenuItem value="">
                  <em>{t("pharmacy.allCategories")}</em>
                </MenuItem>
                {medicineCategory.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {t("pharmacy.totalRecords", "Total Records")}:
            </Typography>
            <Typography fontWeight="bold" color="primary">
              {stockMedicines.length}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Content Section */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: blueGrey[50],
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            borderRadius: 0,
            boxShadow: "none",
            position: "relative",
          }}
          ref={tableRef}
        >
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255,255,255,0.7)",
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 50, bgcolor: blue[50] }}>
                  <Typography fontWeight={700}>#</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 150, bgcolor: blue[50] }}>
                  <Typography fontWeight={700}>
                    {t("pharmacy.category")}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 200, bgcolor: blue[50] }}>
                  <Typography fontWeight={700}>
                    {t("pharmacy.medicineName")}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120, bgcolor: blue[50] }}>
                  <Typography fontWeight={700}>
                    {t("pharmacy.expiryDate")}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 100, bgcolor: blue[50] }}>
                  <Typography fontWeight={700}>
                    {t("pharmacy.quantity")}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120, bgcolor: blue[50] }}>
                  <Typography fontWeight={700}>
                    {t("pharmacy.sellPrice")} (؋)
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120, bgcolor: blue[50] }}>
                  <Typography fontWeight={700}>
                    {t("pharmacy.action", "Action")}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockMedicines
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((stock, index) => (
                  <TableRow
                    key={stock.id || index}
                    hover
                    sx={{
                      "&:nth-of-type(even)": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{stock.category_display}</TableCell>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {stock.medicine_display}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor:
                            new Date(stock.expiry_date) < new Date()
                              ? "error.light"
                              : "transparent",
                          color:
                            new Date(stock.expiry_date) < new Date()
                              ? "error.contrastText"
                              : "inherit",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: "inline-block",
                        }}
                      >
                        {stock.expiry_date}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor:
                            stock.quantity <= 10 ? "warning.light" : green[100],
                          color:
                            stock.quantity <= 10
                              ? "warning.contrastText"
                              : green[800],
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: "inline-block",
                          fontWeight: "bold",
                        }}
                      >
                        {stock.quantity}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {parseFloat(stock.sell_price).toFixed(2)} ؋
                    </TableCell>

                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(stock.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ borderTop: `1px solid ${blueGrey[200]}`, bgcolor: "white" }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={stockMedicines.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": {
                minHeight: "56px",
              },
            }}
          />
        </Box>
      </Box>

      {/* Footer Section */}
      <Box
        sx={{
          padding: 2,
          backgroundColor: "white",
          borderTop: `1px solid ${blueGrey[200]}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownloadPDF}
          startIcon={<PictureAsPdf />}
          sx={{
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          {t("pharmacy.downloadPdf")}
        </Button>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.secondary"
          >
            {t("pharmacy.totalValue")}:
          </Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: blue[800],
              backgroundColor: blue[50],
              px: 2,
              py: 1,
              borderRadius: 1,
            }}
          >
            {parseFloat(totalAmount.toFixed(2))} ؋
          </Typography>
          <ConfirmDialog />
        </Box>
      </Box>
    </Paper>
  );
};

export default StockMedicine;
